import { DropDownList, DropDownListChangeEvent, DropDownListFilterChangeEvent, DropDownListProps } from '@progress/kendo-react-dropdowns';
import React from 'react';

type DropdownItem<T> = { value: T; text: string; disabled?: boolean; hidden?: boolean };
export type DropdownList<T> = DropdownItem<T>[];

// If this throw an error then go back and make sure all enum values are in the array
export function createDropdownList<T extends string | number>() {
    // Warning: vibe-coded
    return <U extends DropdownItem<T>[]>(items: U & ([T] extends [U[number]['value']] ? unknown : never)): U => items;
}

export type DropdownListWithValueFieldChangeEvent<T = unknown> = Omit<DropDownListChangeEvent, 'value'> & { value: T };
export type DropdownListWithValueFieldProps<T = unknown> = Omit<DropDownListProps, 'data' | 'value' | 'onChange' | 'onFilterChange'> & {
    data: DropdownList<T>;
    value?: T;
    onChange?: (e: DropdownListWithValueFieldChangeEvent<T>) => void;
};

export function DropdownListWithValueField<T>(props: DropdownListWithValueFieldProps<T>) {
    const [filterText, setFilterText] = React.useState('');

    const itemFromValue = React.useCallback((value: any) => props.data?.find((x) => x['value'] === value), [props.data]);

    const onChange = React.useCallback(
        (e: DropDownListChangeEvent) => {
            setFilterText('');
            props.onChange?.({ ...e, value: e.value['value'] });
        },
        [props.onChange]
    );

    const onFilterChange = React.useCallback((e: DropDownListFilterChangeEvent) => {
        setFilterText(e.filter.value);
    }, []);

    const onClose = React.useCallback(() => {
        setFilterText('');
    }, []);

    const data = React.useMemo(() => {
        if (!filterText) {
            return props.data.filter((x) => !x.hidden);
        }
        const lower = filterText.toLowerCase();
        return props.data.filter((x) => !x.hidden && x.text.toLowerCase().includes(lower));
    }, [props.data, filterText]);

    return (
        <DropDownList
            {...props}
            data={data}
            value={itemFromValue(props.value)}
            onChange={onChange}
            onFilterChange={props.filterable ? onFilterChange : undefined}
            onClose={onClose}
            textField="text"
        />
    );
}
